import type { OperationalProfile } from '../../authorization/model.js';
import type { AuthenticatedUser } from '../auth/model.js';
import { escapeAttribute, escapeHtml } from '../html.js';
import { renderAuthenticatedShell } from '../view.js';
import type { PlatformAdministrationModel } from './model.js';

export function renderPlatformAdministration(
  user: AuthenticatedUser,
  clientCount: number,
  model?: PlatformAdministrationModel,
  feedback = '',
): string {
  return renderAuthenticatedShell(
    user,
    clientCount,
    'PLATFORM_ADMIN',
    `
      <main class="workspace" id="platform-admin">
        <header class="page-heading">
          <h1>Platform administration</h1>
          <p>Owner-only application identity and profile administration.</p>
        </header>
        <p class="settings-feedback" data-platform-admin-feedback>${escapeHtml(feedback)}</p>
        <section class="settings-section platform-admin-section" aria-labelledby="users-profiles-title">
          <header>
            <div>
              <h2 id="users-profiles-title">Users / Profiles</h2>
              <p>Operational profiles control landing and dashboard priority only.</p>
            </div>
          </header>
          <p class="platform-security-note"><strong>Security roles are managed in Microsoft Entra ID.</strong> Observed roles are read-only and never authorize from this table.</p>
          ${model === undefined ? '<p>Loading observed application users…</p>' : renderUsers(model)}
        </section>
        <section class="integration-list" aria-label="Reserved platform administration areas">
          ${['Integrations', 'System Settings'].map((section) => `
            <article class="integration-row">
              <div><strong>${section}</strong><p>Reserved for a future owner-managed workflow.</p></div>
              <span class="status-pill planned">Planned</span>
            </article>
          `).join('')}
        </section>
      </main>
    `,
  );
}

function renderUsers(model: PlatformAdministrationModel): string {
  if (model.users.length === 0) {
    return '<p>No authorized users have been observed or bootstrapped.</p>';
  }
  return `
    <div class="platform-user-list" role="table" aria-label="Observed application users">
      ${model.users.map((user) => `
        <form class="platform-user-row" data-user-profile-form data-subject-id="${escapeAttribute(user.subjectId)}">
          <div>
            <strong>${escapeHtml(user.displayName ?? user.email ?? 'Observed Entra user')}</strong>
            <p>${escapeHtml(user.email ?? 'Email not observed')}</p>
            <small>OID: ${escapeHtml(user.subjectId)} · Last seen: ${escapeHtml(formatObservedAt(user.lastObservedAt))}</small>
          </div>
          <div>
            <span class="status-pill">${escapeHtml(user.observedRole ?? 'Not observed')}</span>
            <small>Entra role (read-only)</small>
          </div>
          <label>
            <span>Operational profile</span>
            <select name="operationalProfile">
              ${model.profiles.map((profile) => profileOption(profile, user.operationalProfile)).join('')}
            </select>
          </label>
          <button class="table-action" type="submit">Save profile</button>
        </form>
      `).join('')}
    </div>
  `;
}

function profileOption(profile: OperationalProfile, selected: OperationalProfile): string {
  return `<option value="${profile}"${profile === selected ? ' selected' : ''}>${escapeHtml(profileLabel(profile))}</option>`;
}

function profileLabel(profile: OperationalProfile): string {
  return profile.split('_').map((part) =>
    part.charAt(0) + part.slice(1).toLowerCase()).join(' ');
}

function formatObservedAt(value: string | null): string {
  if (value === null) return 'Not yet signed in';
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.valueOf()) ? 'Unavailable' : timestamp.toLocaleString();
}
